import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

const SKIP_AUTH_PATHS = ['/auth/login', '/auth/refresh', '/health'];

function shouldSkipAuth(url: string): boolean {
  // Match public endpoints only. The protected rebiller Customers endpoint also
  // ends in /health; substring matching dropped both its token and 401 refresh.
  try {
    const path = new URL(url, 'http://onegate.local').pathname
      .replace(/^\/api(?=\/)/, '').replace(/\/$/, '');
    return SKIP_AUTH_PATHS.includes(path);
  } catch {
    return false;
  }
}

function addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);

  if (shouldSkipAuth(req.url)) {
    return next(req);
  }

  const token = authService.getAccessToken();
  const authedReq = token ? addToken(req, token) : req;

  return next(authedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !shouldSkipAuth(req.url)) {
        const currentUrl = typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search}`
          : undefined;
        return authService.refresh({ redirectOnFailure: false }).pipe(
          switchMap(tokenResponse => {
            if (tokenResponse) {
              const retryReq = addToken(req, tokenResponse.accessToken);
              return next(retryReq);
            }
            authService.handleSessionExpired(currentUrl);
            return throwError(() => error);
          }),
          catchError(() => {
            authService.handleSessionExpired(currentUrl);
            return throwError(() => error);
          }),
        );
      }
      return throwError(() => error);
    }),
  );
};
